/**
 * Mock PaymentMethod Repository
 *
 * 로컬 Mock 데이터를 사용하여 결제수단 데이터를 제공합니다.
 */

import type { PaymentMethod } from '../types/PaymentMethod'
import type { PaymentMethodRepository } from './PaymentMethodRepository'
import { mockPaymentMethods } from '../mocks/mockPaymentMethods'

/**
 * Mock PaymentMethod Repository 구현
 */
export class MockPaymentMethodRepository implements PaymentMethodRepository {
  /**
   * 내부 데이터 저장소 (CRUD 작업 가능하도록 mutable)
   */
  private paymentMethods: PaymentMethod[] = [...mockPaymentMethods]

  /**
   * 지연 시뮬레이션 (네트워크 요청 흉내)
   */
  private delay(ms: number = 50): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * ID 생성
   */
  private generateId(): string {
    return `pm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * 모든 결제수단 조회
   */
  async findAll(): Promise<PaymentMethod[]> {
    await this.delay()
    return this.paymentMethods.map((pm) => ({ ...pm }))
  }

  /**
   * 활성화된 결제수단만 조회
   */
  async findActive(): Promise<PaymentMethod[]> {
    await this.delay()
    return this.paymentMethods
      .filter((pm) => pm.isActive)
      .map((pm) => ({ ...pm }))
  }

  /**
   * ID로 결제수단 조회
   */
  async findById(id: string): Promise<PaymentMethod | null> {
    await this.delay()
    const paymentMethod = this.paymentMethods.find((pm) => pm.id === id)
    return paymentMethod ? { ...paymentMethod } : null
  }

  /**
   * 사용자별 결제수단 조회
   */
  async findByOwnerId(ownerId: string): Promise<PaymentMethod[]> {
    await this.delay()
    return this.paymentMethods
      .filter((pm) => pm.ownerId === ownerId && pm.isActive)
      .map((pm) => ({ ...pm }))
  }

  /**
   * 새 결제수단 생성
   */
  async create(input: Omit<PaymentMethod, 'id'>): Promise<PaymentMethod> {
    await this.delay()
    const newPaymentMethod: PaymentMethod = {
      id: this.generateId(),
      ...input,
    }
    this.paymentMethods.push(newPaymentMethod)
    return { ...newPaymentMethod }
  }

  /**
   * 결제수단 수정
   */
  async update(
    id: string,
    input: Partial<PaymentMethod>
  ): Promise<PaymentMethod> {
    await this.delay()
    const index = this.paymentMethods.findIndex((pm) => pm.id === id)
    if (index === -1) {
      throw new Error(`결제수단을 찾을 수 없습니다: ${id}`)
    }
    const updated = { ...this.paymentMethods[index], ...input, id } as PaymentMethod
    this.paymentMethods[index] = updated
    return { ...updated }
  }

  /**
   * 결제수단 삭제
   */
  async delete(id: string): Promise<void> {
    await this.delay()
    const index = this.paymentMethods.findIndex((pm) => pm.id === id)
    if (index === -1) {
      throw new Error(`결제수단을 찾을 수 없습니다: ${id}`)
    }
    this.paymentMethods.splice(index, 1)
  }
}
